import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { deleteIntroduceVision, getAdminIntroduceVisions } from 'apis/adminIntroduce';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const IntroduceVisionsList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const queryParams = useMemo(() => ({
        pageNumber: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter]);

    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery('introduce-visions', queryParams, getAdminIntroduceVisions);
    const { data: languages = [] } = useActiveLanguagesQuery();
    const deleteMutation = useAdminDeleteMutation('introduce-visions', deleteIntroduceVision);

    useEffect(() => {
        setCurrentPage(1);
    }, [keyword, isActiveFilter, languageCodeFilter]);

    useEffect(() => {
        if (error) toast.error(error.message || 'Tải dữ liệu thất bại');
    }, [error, toast]);

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res.success) { toast.success('Xóa thành công'); } else toast.error(res.message);
        } catch { toast.error('Xóa thất bại'); }
        setDeleteId(null);
    };

    const columns = [
        { field: 'id', header: 'ID' },
        { field: 'introduceMenuName', header: 'Menu Giới thiệu' },
        {
            header: 'Hình ảnh',
            render: (row) => row.image ? <img src={row.image} alt="img" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : <span style={{ color: '#ccc', fontSize: 12 }}>Chưa có</span>,
        },
        { field: 'title', header: 'Tiêu đề' },
        { 
            header: 'Mô tả', 
            render: (row) => row.description ? (
                <div 
                    dangerouslySetInnerHTML={{ __html: row.description }}
                    style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '300px'
                    }}
                />
            ) : '—'
        },
        { field: 'sortOrder', header: 'Thứ tự' },
        { header: 'Trạng thái', render: (row) => <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>{row.isActive ? 'Hoạt động' : 'Không hoạt động'}</span> },
        { header: 'Thao tác', render: (row) => <div className="action-buttons"><button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/introduce-visions/edit/${row.id}`)} title="Sửa"><FiEdit2 size={15} /></button><button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(row.id)} title="Xóa"><FiTrash2 size={15} /></button></div> },
    ];

    return (
        <div className="admin-page">
            <PageHeader title="Tầm nhìn (Visions)" subtitle="Quản lý phần tầm nhìn, sứ mệnh, giá trị cốt lõi" action={<Button onClick={() => navigate('/admin/introduce-visions/create')}>+ Thêm mới</Button>} />
            <div className="filters-bar">
                <div className="filter-item"><Input placeholder="Tìm kiếm..." value={keyword} onChange={e => setKeyword(e.target.value)} /></div>
                <div className="filter-item"><Select value={isActiveFilter} onChange={e => setIsActiveFilter(e.target.value)} options={[{ value: '', label: 'Tất cả trạng thái' }, { value: 'true', label: 'Hoạt động' }, { value: 'false', label: 'Không hoạt động' }]} /></div>
                <div className="filter-item">
                    <Select value={languageCodeFilter} onChange={(e) => setLanguageCodeFilter(e.target.value)}
                        options={[...languages.map(l => ({ value: l.code, label: l.name }))]} />
                </div>
            </div>
            <Table columns={columns} data={data.items} loading={isFetching} pagination={{ currentPage: data.currentPage, totalPages: Math.ceil(data.total / data.pageSize) || 1, onPageChange: setCurrentPage }} />
            <ConfirmationDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Xóa" message="Bạn có chắc chắn muốn xóa không?" />
        </div>
    );
};

export default IntroduceVisionsList;
